import {
  type AreaScore,
  INTEREST_AREAS,
  type InterestArea,
  interestMap,
  simsFor,
  useAlumni,
  useAlumniActions,
  useAlumniQuestions,
  useClubs,
  useInterestLikes,
  useSetInterestLikes,
  useYearBook,
} from '@shkola/api';
import type { SubjectIcon } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  SubjectArt,
  Tabs,
  grad,
  pillClass,
} from '../design/forms';
import { ArrowUpRightIcon, CheckIcon, HeartIcon, PaperPlaneTiltIcon } from '../design/icons';

/*
 * «Моє майбутнє» in «Форми 2» (28.10): the title on the paper, the map or the alumni to choose beside it.
 * The map: each area of interests a white card on the paper with its line picture — how it goes (by the
 * grades) and how much it is liked as two thin bars, what shows it, what to try as pills ↗ (the clubs, a
 * simulation), the professions quiet; «Мені цікаво» a pill with a heart. The alumni: the areas as chips to
 * narrow, each graduate a white card — the picture of the field, the story, the answers on the paper.
 */

/**
 * The page's title with its controls: beside the title on a wide screen, in a row under it on a phone
 * (beside the big title they would squeeze it).
 */
function TitleRow({ children, ...head }: Parameters<typeof PageHead>[0]) {
  return (
    <div className="flex flex-col gap-3">
      <PageHead {...head}>
        {children && (
          <span className="hidden flex-wrap items-center gap-2 lg:flex">{children}</span>
        )}
      </PageHead>
      {children && <div className="flex flex-wrap items-center gap-2 lg:hidden">{children}</div>}
    </div>
  );
}

type Tab = 'map' | 'alumni';

/** An area's picture: the line drawing of its main subject on its colour. */
const AREA_ART: Record<InterestArea, { icon: SubjectIcon; tone: Grad }> = {
  tech: { icon: 'Hammer', tone: 'apricot' },
  it: { icon: 'Monitor', tone: 'aqua' },
  math: { icon: 'Sigma', tone: 'sky' },
  nature: { icon: 'Leaf', tone: 'mint' },
  words: { icon: 'PenLine', tone: 'butter' },
  society: { icon: 'Landmark', tone: 'sand' },
  art: { icon: 'Palette', tone: 'pink' },
  sport: { icon: 'Dumbbell', tone: 'coral' },
};
const artOf = (key: InterestArea): { icon: SubjectIcon; tone: Grad } =>
  AREA_ART[key] ?? { icon: 'BookMarked', tone: 'stone' };

/** the emoji of the data (a club's) never shows: our pictures only */
const plain = (s: string) => s.replace(/\p{Extended_Pictographic}️?/gu, '').trim();

/** A thin bar: how much, in its colour. */
function Bar({ value, tone }: { value: number | null; tone: Grad }) {
  return (
    <span
      className="block h-2 w-full overflow-hidden rounded-full"
      style={{ backgroundColor: PAPER }}
    >
      <span
        className="block h-full rounded-full"
        style={{
          width: `${Math.round(100 * (value ?? 0))}%`,
          backgroundImage: `linear-gradient(90deg, ${GRAD[tone].to}, ${GRAD[tone].ink})`,
        }}
      />
    </span>
  );
}

function AreaCard({
  score,
  self,
  onLike,
  clubs,
}: {
  score: AreaScore;
  self: boolean;
  onLike: () => void;
  clubs: ReadonlyArray<{ id: string; title: string; emoji: string | null }>;
}) {
  const { t } = useTranslation();
  const { area } = score;
  const art = artOf(area.key);
  const sim = area.subjects.flatMap((s) => simsFor({ subject: s }))[0];
  return (
    <article className="flex min-w-0 flex-col gap-4 rounded-[28px] bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <SubjectArt icon={art.icon} tone={art.tone} size={48} />
        {self ? (
          <button
            type="button"
            aria-pressed={score.liked}
            onClick={onLike}
            className={pillClass(score.liked ? 'dark' : 'light', 'sm')}
          >
            {score.liked ? t('future.liked') : t('future.like')}
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full ring-[1.5px] ring-current ring-inset">
              <HeartIcon size={12} weight={score.liked ? 'fill' : 'bold'} aria-hidden />
            </span>
          </button>
        ) : (
          score.liked && (
            <span
              className="inline-flex h-6 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium"
              style={{ backgroundImage: grad('pink'), color: GRAD.pink.ink }}
            >
              <HeartIcon size={12} weight="fill" aria-hidden />
              {t('future.liked')}
            </span>
          )
        )}
      </div>
      <h2
        className="-mt-1 font-display text-[23px] leading-[28px] font-light tracking-[-0.025em]"
        style={{ color: INK }}
      >
        {area.name}
      </h2>
      <div className="grid grid-cols-[96px_1fr] items-center gap-x-3 gap-y-2 text-[13px]">
        <span style={{ color: INK_SOFT }}>{t('future.strength')}</span>
        {score.strength === null ? (
          <span className="text-[12.5px]" style={{ color: INK_FAINT }}>
            {t('future.noGrades')}
          </span>
        ) : (
          <Bar value={score.strength} tone="sky" />
        )}
        <span style={{ color: INK_SOFT }}>{t('future.interest')}</span>
        <Bar value={score.interest} tone="pink" />
      </div>
      {score.evidence.length > 0 && (
        <p className="text-[13.5px] leading-[20px]" style={{ color: INK_SOFT }}>
          {score.evidence.slice(0, 4).map(plain).join(' · ')}
        </p>
      )}
      <div className="mt-auto flex flex-col gap-2">
        {(clubs.length > 0 || sim) && (
          <>
            <p className="text-[13px] font-medium" style={{ color: INK }}>
              {t('future.try')}
            </p>
            <p className="flex flex-wrap gap-1.5">
              {clubs.slice(0, 2).map((c) => (
                <Link key={c.id} to="/clubs" className={pillClass('light', 'sm')}>
                  <span className="max-w-[160px] truncate">{plain(c.title)}</span>
                  <PillIcon icon={ArrowUpRightIcon} size="sm" />
                </Link>
              ))}
              {sim && (
                <Link
                  to="/lab/sim/$slug"
                  params={{ slug: sim.slug }}
                  className={pillClass('light', 'sm')}
                >
                  <span className="max-w-[160px] truncate">{sim.title}</span>
                  <PillIcon icon={ArrowUpRightIcon} size="sm" />
                </Link>
              )}
            </p>
          </>
        )}
        <p className="text-[12.5px] leading-[18px]" style={{ color: INK_FAINT }}>
          {t('future.professions')}: {area.professions.join(', ')}
        </p>
      </div>
    </article>
  );
}

function MapTab() {
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const book = useYearBook(child?.memberId ?? '');
  const likes = useInterestLikes(child?.memberId ?? '');
  const setLikes = useSetInterestLikes(ctx.schoolId, child?.memberId ?? '');
  const clubs = useClubs(ctx.schoolId).data ?? [];
  if (book.isPending || likes.isPending) return <Spinner />;
  if (!book.data) return null;
  const mine = likes.data ?? [];
  const map = interestMap(book.data, mine);
  const self = ctx.kind === 'student';
  const toggle = (key: InterestArea) =>
    setLikes.mutate(
      (mine.includes(key) ? mine.filter((x) => x !== key) : [...mine, key]) as InterestArea[],
    );
  const grade = child?.class?.grade;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {map.map((score) => (
        <AreaCard
          key={score.area.key}
          score={score}
          self={self}
          onLike={() => toggle(score.area.key)}
          clubs={clubs.filter(
            (c) =>
              score.area.clubs.some((stem) => c.title.toLowerCase().includes(stem)) &&
              (grade === undefined || (grade >= c.gradeFrom && grade <= c.gradeTo)),
          )}
        />
      ))}
    </div>
  );
}

const textArea =
  'w-full rounded-[16px] bg-white p-3.5 text-[15px] leading-6 text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] outline-none ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]';

function Ask({ alumnusId }: { alumnusId: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useAlumniActions(ctx.schoolId);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  if (ctx.kind !== 'student') return null;
  if (sent)
    return (
      <p
        className="flex items-center gap-2 self-start rounded-[10px] px-3 py-2 text-[13.5px] font-medium"
        style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
      >
        <CheckIcon size={14} weight="bold" aria-hidden />
        {t('future.sent')}
      </p>
    );
  if (!open)
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${pillClass('dark')} mt-auto self-start`}
      >
        {t('future.ask')}
        <PillIcon icon={PaperPlaneTiltIcon} />
      </button>
    );
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void actions.ask
          .mutateAsync({ alumnusId, studentId: ctx.child.memberId, question: text })
          .then(() => setSent(true));
      }}
    >
      <textarea
        rows={3}
        maxLength={1000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('future.askPlaceholder')}
        className={textArea}
      />
      <button
        type="submit"
        disabled={!text.trim() || actions.ask.isPending}
        className={`${pillClass('dark')} self-start disabled:opacity-35`}
      >
        {t('future.send')}
        <PillIcon icon={PaperPlaneTiltIcon} />
      </button>
    </form>
  );
}

/** The office passes questions on and writes the answers down. */
function AnswerQuestions() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const data = useAlumni(ctx.schoolId).data;
  const questions = (useAlumniQuestions({ schoolId: ctx.schoolId }).data ?? []).filter(
    (q) => !q.answer,
  );
  const actions = useAlumniActions(ctx.schoolId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  return (
    <Block>
      <Head title={t('future.toAnswer')} count={questions.length} />
      {questions.length === 0 && (
        <p className="text-[15px]" style={{ color: INK_SOFT }}>
          {t('future.noQuestions')}
        </p>
      )}
      <div className={`grid grid-cols-1 gap-2 ${questions.length > 1 ? 'xl:grid-cols-2' : ''}`}>
        {questions.map((q) => {
          const who = data?.alumni.find((a) => a.id === q.alumnusId);
          return (
            <div
              key={q.id}
              className="flex flex-col gap-2.5 rounded-[20px] p-4"
              style={{ backgroundColor: PAPER }}
            >
              <p className="text-[13px]" style={{ color: INK_SOFT }}>
                {who?.fullName}
              </p>
              <p className="-mt-1.5 text-[15px] font-medium" style={{ color: INK }}>
                {q.question}
              </p>
              <textarea
                rows={2}
                placeholder={t('future.answer')}
                value={drafts[q.id] ?? ''}
                onChange={(e) => setDrafts({ ...drafts, [q.id]: e.target.value })}
                className={textArea}
              />
              <button
                type="button"
                disabled={!(drafts[q.id] ?? '').trim()}
                onClick={() => actions.answer.mutate({ id: q.id, answer: drafts[q.id] ?? '' })}
                className={`${pillClass('dark')} self-start pr-4.5 disabled:opacity-35`}
              >
                {t('future.save')}
              </button>
            </div>
          );
        })}
      </div>
    </Block>
  );
}

function AlumniTab() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const data = useAlumni(ctx.schoolId);
  const mine = useAlumniQuestions({
    studentId: ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child.memberId : '',
  });
  const [area, setArea] = useState<InterestArea>();
  if (data.isPending) return <Spinner />;
  const alumni = (data.data?.alumni ?? []).filter((a) => !area || a.area === area);
  const answers = data.data?.answers ?? [];
  const own = ctx.kind === 'student' || ctx.kind === 'guardian' ? (mine.data ?? []) : [];
  const chip = (on: boolean) =>
    `inline-flex h-10 shrink-0 items-center gap-2 rounded-full pr-3.5 pl-1.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;
  return (
    <div className="flex flex-col gap-4">
      {ctx.kind === 'admin' && <AnswerQuestions />}
      <Block>
        <p className="max-w-2xl text-[15px] leading-[22px]" style={{ color: INK_SOFT }}>
          {t('future.alumniIntro')}
        </p>
        {/* the areas as chips: narrow the graduates to one field */}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {INTEREST_AREAS.map((a) => {
            const on = area === a.key;
            const art = artOf(a.key);
            return (
              <button
                key={a.key}
                type="button"
                aria-pressed={on}
                onClick={() => setArea(on ? undefined : a.key)}
                className={chip(on)}
                style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
              >
                <SubjectArt icon={art.icon} tone={art.tone} size={28} />
                {a.name}
              </button>
            );
          })}
        </div>
      </Block>
      {/* the graduates flow in two columns: a short story leaves no hole beside a long one */}
      <div className="columns-1 gap-4 md:columns-2">
        {alumni.map((a) => {
          const qa = answers.filter((x) => x.alumnusId === a.id);
          const asked = own.filter((x) => x.alumnusId === a.id && !x.answer);
          const art = artOf(a.area);
          return (
            <article
              key={a.id}
              className="mb-4 flex min-w-0 break-inside-avoid flex-col gap-4 rounded-[28px] bg-white p-6"
            >
              <div className="flex items-center gap-4">
                <span
                  className="flex size-16 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundImage: grad(art.tone) }}
                >
                  <SubjectArt icon={art.icon} tone={art.tone} size={44} />
                </span>
                <div className="min-w-0">
                  <p
                    className="font-display text-[24px] leading-[30px] font-light tracking-[-0.025em]"
                    style={{ color: INK }}
                  >
                    {a.fullName}
                  </p>
                  <p className="text-[13.5px]" style={{ color: INK_SOFT }}>
                    {a.profession} · {t('future.graduated', { year: a.graduatedYear })}
                  </p>
                  {a.university && (
                    <p className="text-[12.5px]" style={{ color: INK_FAINT }}>
                      {a.university}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-[15px] leading-[23px]" style={{ color: INK }}>
                {a.story}
              </p>
              {qa.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {qa.slice(0, 2).map((x) => (
                    <li
                      key={x.id}
                      className="rounded-[18px] p-3.5 text-[14px] leading-[21px]"
                      style={{ backgroundColor: PAPER }}
                    >
                      <p className="font-medium" style={{ color: INK }}>
                        «{x.question}»
                        {x.askedBy && (
                          <span className="ml-1 font-normal" style={{ color: INK_FAINT }}>
                            — {t('future.askedBy', { class: x.askedBy })}
                          </span>
                        )}
                      </p>
                      <p className="mt-1" style={{ color: INK_SOFT }}>
                        {x.answer}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {asked.map((x) => (
                <p
                  key={x.id}
                  className="flex flex-wrap items-center gap-2 text-[14px]"
                  style={{ color: INK_SOFT }}
                >
                  «{x.question}»
                  <span
                    className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium"
                    style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
                  >
                    {t('future.waiting')}
                  </span>
                </p>
              ))}
              <Ask alumnusId={a.id} />
            </article>
          );
        })}
      </div>
    </div>
  );
}

/** «Моє майбутнє»: the map of interests and the alumni who tell about their professions. */
export function FuturePage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const family = ctx.kind === 'student' || ctx.kind === 'guardian';
  const [tab, setTab] = useState<Tab>(family ? 'map' : 'alumni');
  return (
    <div className="flex flex-col gap-4">
      <TitleRow
        title={t('future.title')}
        sub={
          family ? (
            <span className="block max-w-2xl text-[15px] leading-[22px]">
              {ctx.kind === 'student' ? t('future.intro') : t('future.introFamily')}
            </span>
          ) : undefined
        }
      >
        {family && (
          <Tabs
            label={t('future.title')}
            value={tab}
            onChange={setTab}
            options={(['map', 'alumni'] as const).map((k) => ({ id: k, label: t(`future.${k}`) }))}
          />
        )}
      </TitleRow>
      {tab === 'map' && family ? <MapTab /> : <AlumniTab />}
    </div>
  );
}
